import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { formatCents, pctOf } from "@shared/money";
import { lineAdd, linePatch } from "@shared/schemas/bookings";
import { evaluation, parseInput } from "@shared/validation/issues";
import { errorMessage } from "@web/lib/api";
import { changedValues, useFormValidation } from "@web/lib/formValidation";
import { recordValidationQuery } from "@web/lib/validation";
import { pctLabel } from "@web/lib/format";
import { api, catalogQuery, type BookingDetail, type BookingLine, type CatalogItem } from "@web/lib/queries";
import { BottomSheet, Button, EmptyState, MoneyField, Picker, Row, RowGroup, SegmentedControl, Skeleton, Stack, Text, TextField, UnsavedChanges, ValidationSummary, useToast } from "@web/ui";

const KINDS = [
  { value: "fee", label: "Fee" },
  { value: "addon", label: "Add-on" },
  { value: "extra", label: "Extra" },
  { value: "discount", label: "Discount" },
  { value: "late_fee", label: "Late fee" },
];
const unitLabel = (pricing: string) => (pricing === "hourly" ? "/hr" : pricing === "per_guest" ? "/guest" : "");

/** `+ Add`: catalog items for the venue + date (resolved amounts) or a custom line → POST /lines. */
export function AddLineSheet({ b, open, onClose }: { b: BookingDetail; open: boolean; onClose: () => void }) {
  const toast = useToast();
  const [mode, setMode] = useState<"catalog" | "custom">("catalog");
  const [kind, setKind] = useState("fee");
  const [name, setName] = useState("");
  const [qty, setQty] = useState("1");
  const [unit, setUnit] = useState<number | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [confirmDiscount, setConfirmDiscount] = useState<CatalogItem | null>(null);
  const catalog = useQuery({ ...catalogQuery({ venueId: b.venueId, date: b.eventDate }), enabled: open });
  const custom = { kind, name, qty: Number(qty), unitAmount: unit };
  const local = mode === "custom" ? parseInput(lineAdd, custom, { type: "booking_line" }, "booking.save").issues : [];
  const validation = useFormValidation(evaluation(local, false));
  const { field } = validation;

  async function add(body: unknown, key: string) {
    const parsed = parseInput(lineAdd, body, { type: "booking_line" }, "booking.save");
    if (!parsed.success) { validation.report(parsed.issues); return; }
    if (!validation.begin("booking.save")) return;
    setBusy(key);
    try {
      await api.addLine(b.id, parsed.data);
      toast.success("Line added");
      onClose();
    } catch (e) {
      validation.reject(e);
      toast.error(errorMessage(e));
    } finally {
      setBusy(null);
      validation.finish();
    }
  }

  return (
    <BottomSheet
      open={open}
      onClose={() => { if (!validation.busy) onClose(); }}
      title="Add line"
      footer={
        confirmDiscount ? <Button loading={busy === confirmDiscount.id} onClick={() => add({ catalogItemId: confirmDiscount.id, confirmPayInFull: true }, confirmDiscount.id)}>Add discount · full payment due today</Button> : mode === "custom" ? (
          <Button loading={busy === "custom"} onClick={() => add(custom, "custom")}>
            Add line
          </Button>
        ) : undefined
      }
    >
      <Stack gap={4}>
        <UnsavedChanges dirty={!validation.busy && Boolean(name || kind !== "fee" || qty !== "1" || unit !== null || confirmDiscount)} />
        <ValidationSummary result={validation.result} local unavailable={validation.unavailable} />
        {confirmDiscount ? <Stack gap={3}>
          <Text variant="headline">Pay in full today?</Text>
          <Text>A 5% discount ({formatCents(pctOf(Math.max(0, b.lines.filter(l => l.kind !== "processing_fee").reduce((sum, l) => sum + l.amount, 0)), "5"))}) will be added before the processing fee. The remaining total will be due today. Existing payments are retained.</Text>
          <Button variant="secondary" disabled={validation.busy} onClick={() => validation.edit(() => setConfirmDiscount(null))}>Back to catalog</Button>
        </Stack> : <>
        <SegmentedControl label="Line source" items={[{ key: "catalog", label: "Catalog" }, { key: "custom", label: "Custom" }]} value={mode} onChange={v => validation.edit(() => setMode(v))} />
        {mode === "catalog" ? (
          catalog.isLoading ? (
            <Skeleton count={4} />
          ) : !catalog.data?.length ? (
            <EmptyState title="No catalog items" body="Add packages and fees under Settings › Catalog." />
          ) : (
            <RowGroup>
              {catalog.data.map((c) => (
                <Row
                  key={c.id}
                  title={c.name}
                  subtitle={`${c.kind}${c.description ? ` · ${c.description}` : ""}`}
                  trailing={
                    <Text as="span" variant="body" tabular>
                      {busy === c.id ? "…" : c.pricingAction === "pay_in_full_discount" ? "5%" : c.pricingAction === "late_fee" ? "Overdue %" : `${formatCents(c.resolvedAmount)}${unitLabel(c.pricing)}`}
                    </Text>
                  }
                  onClick={() => { if (!validation.busy) { if (c.pricingAction === "pay_in_full_discount") validation.edit(() => setConfirmDiscount(c)); else void add({ catalogItemId: c.id }, c.id); } }}
                />
              ))}
            </RowGroup>
          )
        ) : (
          <Stack gap={3}>
            <Picker label="Kind" value={kind} onChange={v => validation.edit(() => setKind(v))} options={KINDS} {...field("kind")} />
            <TextField label="Name" value={name} onChange={(e) => validation.edit(() => setName(e.target.value))} autoFocus {...field("name")} />
            <TextField label="Quantity" type="number" inputMode="numeric" min={1} value={qty} onChange={(e) => validation.edit(() => setQty(e.target.value))} {...field("qty")} />
            <MoneyField label={kind === "discount" ? "Discount amount" : "Unit amount"} value={unit} onChange={v => validation.edit(() => setUnit(v))} hint={kind === "discount" ? "Stored as a negative line" : undefined} {...field("unitAmount")} />
          </Stack>
        )}
        </>}
      </Stack>
    </BottomSheet>
  );
}

/** Tap a line: edit name / qty / unit (or the % for the processing fee); Remove. */
export function EditLineSheet({ b, line, onClose }: { b: BookingDetail; line: BookingLine | null; onClose: () => void }) {
  const toast = useToast();
  const [name, setName] = useState(line?.name ?? "");
  const [qty, setQty] = useState(String(line?.qty ?? 1));
  const [unit, setUnit] = useState<number | null>(line ? (line.kind === "discount" ? Math.abs(line.unitAmount) : line.unitAmount) : null);
  const [pct, setPct] = useState(line?.pct ?? "");
  const [busy, setBusy] = useState<"save" | "remove" | null>(null);
  const saved = useQuery(recordValidationQuery("booking", b.id));
  const isFee = line?.kind === "processing_fee";
  const body = isFee ? changedValues({ pct, name: name.trim() }, { pct: line?.pct ?? "", name: line?.name ?? "" }) : changedValues({ name: name.trim(), qty: Number(qty), unitAmount: unit }, { name: line?.name ?? "", qty: Number(String(line?.qty ?? 1)), unitAmount: line ? (line.kind === "discount" ? Math.abs(line.unitAmount) : line.unitAmount) : null });
  const parsed = parseInput(linePatch, body, { type: "booking_line", id: line?.id }, "booking.save");
  const validation = useFormValidation(evaluation(parsed.issues, false));
  const { field } = validation;
  if (!line) return null;

  async function save() {
    if (!parsed.success || !validation.begin("booking.save")) return;
    setBusy("save");
    try {
      await api.patchLine(b.id, line!.id, parsed.data);
      toast.success("Line updated");
      onClose();
    } catch (e) {
      validation.reject(e);
      toast.error(errorMessage(e));
    } finally {
      setBusy(null);
      validation.finish();
    }
  }
  async function remove() {
    if (!validation.begin("audit")) return;
    setBusy("remove");
    try {
      await api.removeLine(b.id, line!.id);
      toast.success("Line removed");
      onClose();
    } catch (e) {
      validation.reject(e);
      toast.error(errorMessage(e));
    } finally {
      setBusy(null);
      validation.finish();
    }
  }

  return (
    <BottomSheet
      open
      onClose={() => { if (!validation.busy) onClose(); }}
      title={isFee ? "Processing fee" : "Edit line"}
      footer={
        <Stack gap={2}>
          <Button loading={busy === "save"} disabled={busy === "remove"} onClick={save}>
            Save
          </Button>
          <Button variant="destructive" loading={busy === "remove"} disabled={busy === "save"} onClick={remove}>
            Remove line
          </Button>
        </Stack>
      }
    >
      <Stack gap={3}>
        <UnsavedChanges dirty={!validation.busy && Object.keys(body).length > 0} />
        <ValidationSummary result={saved.data ? { ...saved.data, issues: saved.data.issues.filter(issue => issue.entity.type === "booking_line" && issue.entity.id === line.id) } : undefined} loading={saved.isFetching} unavailable={saved.isError} onRetry={() => void saved.refetch()} saved />
        <ValidationSummary result={validation.result} local unavailable={validation.unavailable} />
        {isFee ? (
          <TextField label="Fee %" type="number" inputMode="decimal" min={0} step="0.1" value={pct} onChange={(e) => validation.edit(() => setPct(e.target.value))} hint={`Currently ${pctLabel(line.pct ?? "0")} of every other line = ${formatCents(line.amount)}`} {...field("pct")} />
        ) : (
          <>
            <TextField label="Name" value={name} onChange={(e) => validation.edit(() => setName(e.target.value))} {...field("name")} />
            <TextField label="Quantity" type="number" inputMode="numeric" min={1} value={qty} onChange={(e) => validation.edit(() => setQty(e.target.value))} {...field("qty")} />
            <MoneyField label={line.kind === "discount" ? "Discount amount" : "Unit amount"} value={unit} onChange={v => validation.edit(() => setUnit(v))} {...field("unitAmount")} />
          </>
        )}
      </Stack>
    </BottomSheet>
  );
}
